import type {Dispatch, ThunkAction} from 'src/reducers';
import type {RouteAction} from 'src/action-creators/route';
import type {Task} from 'src/types/data-export-report';
import type {RawQuery} from 'src/types/router';
import type {ReactNode} from 'react';

import {thunkify as flow} from 'src/utils/thunks';
import {camel} from 'src/utils';
import {key, cached, fetching} from 'src/utils/redux';

import * as reduxApi from 'src/utils/redux-api-v2';
import * as reduxApiBff from 'src/utils/redux-api-bff';
import {
  pushModal,
  showGenericError,
  showGenericAlert,
} from 'src/action-creators/modal';
import {
  DataExportInit,
  DataExportSuccess,
} from 'src/components/modals/data-export';


export type ReceiveCountAction = {
  type: 'reports/receiveCount';
  payload: number;
};
export type ReceiveTasksAction = {
  type: 'reports/receiveTasks';
  payload: {
    tasks: Task[];
    count: number;
  };
};
export type ReceiveTaskAction = {
  type: 'reports/receiveTask';
  payload: Task;
};
export type ReportAction = ReceiveTasksAction | ReceiveTaskAction | RouteAction;

export const RECEIVE_TASK = 'reports/receiveTask';
export const RECEIVE_TASKS = 'reports/receiveTasks';
export const RECEIVE_COUNT = 'reports/receiveCount';
export const REPORT_TABLE_ROW_FETCH_LIMIT = 10;

const receiveTasks = (payload: any) => ({
  type: RECEIVE_TASKS,
  payload,
});

const receiveTask = (payload: any): ReportAction => ({
  type: RECEIVE_TASK,
  payload,
});

// TODO (kyle): for any tasks in progress, we may want to start polling.
export const getDataExportReports: (offset?: number) => ThunkAction<unknown> =
  flow(
    key(() => 'getDataExportReports'),
    cached((response) => receiveTasks(camel(response)), {ttl: 2000}),
    fetching(),
  )(
    (offset: number = 0) =>
      (dispatch: Dispatch) =>
        dispatch(
          reduxApi.get(
            `data-exports?offset=${offset}&limit=${REPORT_TABLE_ROW_FETCH_LIMIT}&include_count=1`,
          ),
        ),
  );

// NOTE (kyle): the polling function fetches and checks the task status so we
// delegate to it.
export const getSingleExportReport =
  (taskId: string): ThunkAction<void> =>
  async (dispatch: Dispatch) => {
    dispatch(startPolling(taskId));
  };

export const startBffDataExport =
  ({
    pathname,
    data,
    query,
    CustomContent,
    modalVersion,
  }: {
    pathname: string;
    data?: Readonly<{
      [key: string]: unknown;
    }>;
    query?: RawQuery;
    CustomContent?: string | Node;
    modalVersion?: 'genesis';
  }): ThunkAction<void> =>
  async (dispatch: Dispatch) => {
    try {
      dispatch(
        startDataExport(
          pathname,
          data,
          query,
          CustomContent,
          modalVersion,
          true,
        ),
      );
    } catch (e: any) {
      console.log('Something went wrong with bff Data export');
    }
  };

export const startDataExport =
  (
    pathname: string,
    data?: Readonly<{
      [key: string]: unknown;
    }>,
    query?: RawQuery,
    CustomContent?: string | Node,
    modalVersion?: 'genesis',
    isBffApi: boolean = false,
  ): ThunkAction<void> =>
  async (dispatch: Dispatch) => {
    const api = isBffApi ? reduxApiBff : reduxApi;
    try {
      const response = await dispatch(api.post(pathname, data, query));
      dispatch(
        pushModal({
          type: DataExportInit,
          // @ts-expect-error - TS2353 - Object literal may only specify known properties, and 'CustomContent' does not exist in type 'BasicModal'.
          CustomContent,
          topmost: true,
          modalVersion,
        }),
      );
      await dispatch(startPolling(response.task_id, isBffApi));
      dispatch(
        pushModal({type: DataExportSuccess, topmost: true, modalVersion}),
      );
    } catch (error: any) {
      if (
        error instanceof reduxApi.ApiError &&
        error.responseBody &&
        // @ts-expect-error - TS2339 - Property 'errors' does not exist on type '{}'.
        error.responseBody.errors &&
        // @ts-expect-error - TS2339 - Property 'errors' does not exist on type '{}'.
        (Array.isArray(error.responseBody.errors) ||
          // @ts-expect-error - TS2339 - Property 'errors' does not exist on type '{}'.
          typeof error.responseBody.errors === 'string') &&
        // @ts-expect-error - TS2339 - Property 'errors' does not exist on type '{}'.
        error.responseBody.errors.includes('ResourceTaskInProgress')
      ) {
        dispatch(
          showGenericAlert({
            title: 'Please wait for the export in progress',
            text: 'An export is already in progress, and you will receive a notification when it is ready.  You may request another export as soon as the current export is ready.',
            confirmText: 'Okay',
            topmost: true,
            modalVersion,
          }),
        );
      } else {
        dispatch(
          showGenericError({
            title: 'Request failed',
            text: 'We could not process your request at this time. Refresh the page and request the report again.',
            topmost: true,
            modalVersion,
          }),
        );
      }
    }
  };

// NOTE (kyle): for simplicity's sake i'm not adding this to redux,
// but we could if we wanted to give components access to the state.
const polls = new Map();
export const startPolling =
  (taskId: string, isBffApi: boolean = false): ThunkAction<Task> =>
  (dispatch: Dispatch): Promise<Task> =>
    new Promise(
      (
        resolve: (result: Promise<never>) => void,
        reject: (error?: any) => void,
      ) => {
        let timeout = 2000;

        const poll = async () => {
          const currentTimeoutId = polls.get(taskId);

          if (currentTimeoutId) {
            clearTimeout(currentTimeoutId);
          }

          let task;
          try {
            const api = isBffApi ? reduxApiBff : reduxApi;
            task = camel(
              await dispatch(api.get(`data-exports/${taskId}/status`)),
            );
          } catch (error: any) {
            reject(error);
            return;
          }

          dispatch(receiveTask(camel(task)));

          if (['INIT', 'PROCESSING'].includes(task.status)) {
            timeout = Math.min(timeout * 2, 30000);
            polls.set(taskId, setTimeout(poll, timeout));
          } else {
            resolve(task);
            polls.delete(taskId);
          }
        };

        poll();
      },
    );
